import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {summarizeContextPack} from './contextPackConstants';

// El estado del Paquete de Contexto, en un solo lugar: lo consumen el checklist de la ficha
// (`MemoryContextPack`) y la guía del acto Alimenta del Recorrido (`FeedStarterGuide`).
//
// `refreshToken` es un contador monotónico —el mismo mecanismo que `ingestToken`, `connectToken` y
// `openConnectToken` en el resto del Recorrido— que sube cada vez que algo pudo cambiar la
// respuesta: entró material, se re-etiquetó un documento o se borró uno. Sin esto, el checklist se
// pedía SOLO al montar: subías un brief, la tabla de al lado se actualizaba sola y el strip seguía
// diciendo "2/5" hasta un F5.
//
// ⚠️ Este endpoint va SIN `{cache: true}` a propósito. El TTL de 30s del APIClient es exactamente la
// ventana de desactualización que este hook viene a cerrar: un refetch disparado por el token
// dentro de esos 30s devolvería la respuesta vieja y el bug volvería disfrazado. Si alguna vez hace
// falta deduplicar, la vía es limpiar la entrada del cache tras cada mutación, no prender el flag.
export function useContextPack(clientId, {refreshToken = 0, mediaPlanAvailable} = {}) {
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [pack, setPack] = useState(null);          // {items, core_present, core_total}
    const alive = useRef(true);

    useEffect(() => {
        alive.current = true;
        return () => { alive.current = false; };
    }, []);

    const fetchPack = useCallback((silent) => {
        if (!silent) setLoading(true);
        // Listado: se detecta por presencia de `items` (array), no por {status} — el backend manda
        // el array directo. Un `NETWORK_ERROR` del APIClient no trae `items` y cae acá como fallo.
        ClientMemoryApi.getContextPack(clientId).then((data) => {
            if (!alive.current) return;
            if (data && Array.isArray(data.items)) {
                setPack(data);
                setFailed(false);
            } else {
                setFailed(true);
            }
            setLoading(false);
        }).catch(() => {
            // `APIClient.call()` no rechaza (un blip vuelve como {status:'NETWORK_ERROR'}), así que
            // acá sólo cae un error del propio `.then`. Sin este catch, `loading` quedaba en true
            // para siempre y la pantalla mostraba un spinner eterno.
            if (!alive.current) return;
            setFailed(true);
            setLoading(false);
        });
    }, [clientId]);

    // Cambiar de cliente NO es un refresco: es otro cliente. Sin esto, el efecto de abajo salía
    // silencioso (porque ya había corrido una vez) y el checklist del cliente ANTERIOR se quedaba en
    // pantalla —con sus tildes de "ya lo tenés"— hasta que llegara la respuesta del nuevo. La ruta
    // `/client/:id/memory` no remonta el componente al cambiar de id, así que pasa de verdad.
    const firstRun = useRef(true);
    useEffect(() => {
        firstRun.current = true;
        setPack(null);
        setFailed(false);
        setLoading(true);
    }, [clientId]);

    // Montaje: con `loading`. Refrescos por token: SILENCIOSOS — lo que ya está en pantalla no
    // tiene que parpadear porque se sumó una fila (mismo criterio que `MemoryDocumentsView`).
    useEffect(() => {
        fetchPack(!firstRun.current);
        firstRun.current = false;
    }, [fetchPack, refreshToken]);

    const summary = useMemo(
        () => summarizeContextPack(pack && pack.items, {mediaPlanAvailable}),
        [pack, mediaPlanAvailable]);

    return {loading, failed, pack, summary, refetch: () => fetchPack(false)};
}
